import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Diagnostic data
export function useDiagnostic() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['diagnostic', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('*')
        .eq('user_id', user!.id)
        .not('maturity_level', 'is', null)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

// Projects + deliverables — incluye propios + donde el user es colaborador
export function useProjects() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['projects', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const [ownedRes, collabIdsRes] = await Promise.all([
        supabase.from('participant_projects').select('*').eq('user_id', user.id),
        supabase.from('project_collaborators').select('project_id').eq('user_id', user.id),
      ]);
      if (ownedRes.error) throw ownedRes.error;
      if (collabIdsRes.error) throw collabIdsRes.error;

      const owned = (ownedRes.data ?? []) as any[];
      const collabIds = (collabIdsRes.data ?? [])
        .map((r: any) => r.project_id)
        .filter((id: string) => !owned.some(p => p.id === id));

      let collab: any[] = [];
      if (collabIds.length > 0) {
        const { data: cp, error: cpErr } = await supabase
          .from('participant_projects').select('*').in('id', collabIds);
        if (cpErr) throw cpErr;
        collab = cp ?? [];
      }
      const all = [...owned, ...collab];
      all.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
      return all;
    },
    enabled: !!user?.id,
  });
}

export function useDeliverables() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['deliverables', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_deliverables')
        .select('*')
        .eq('user_id', user!.id)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });
}

// Badges
export function useBadges() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['badges', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_badges')
        .select('*')
        .eq('user_id', user!.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });
}

// Streak (singular): devuelve la fila con mayor current_streak entre los
// scopes activos del usuario. Útil para tarjetas resumen (MetricsPage) que
// solo quieren mostrar UN número. Para renderizar todas las rachas activas
// (multi-card por cohorte + empresa + personal), usar useStreaks().
export function useStreak() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['streak', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_streaks')
        .select('*')
        .eq('user_id', user!.id)
        .order('current_streak', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

// Streaks (plural): devuelve todas las rachas ACTIVAS del usuario con su
// scope resuelto (nombre de cohorte, empresa, o 'Personal'). Filtra las
// rachas históricas de cohortes cerradas — esas quedan en DB pero no se
// exponen al usuario final (decisión #4).
export type ActiveStreak = {
  scope_type: 'cohort' | 'organization' | 'personal';
  scope_id: string | null;
  scope_label: string;
  current_streak: number;
  max_streak: number;
  last_activity_date: string | null;
};

export function useStreaks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['streaks', user?.id],
    queryFn: async (): Promise<ActiveStreak[]> => {
      const { data: rows, error } = await supabase
        .from('participant_streaks')
        .select('scope_type, scope_id, current_streak, max_streak, last_activity_date')
        .eq('user_id', user!.id);
      if (error) throw error;
      if (!rows || rows.length === 0) return [];

      // Resolver nombres de cohortes y organizaciones referenciados
      const cohortIds = Array.from(new Set(
        rows.filter(r => r.scope_type === 'cohort' && r.scope_id).map(r => r.scope_id as string)
      ));
      const orgIds = Array.from(new Set(
        rows.filter(r => r.scope_type === 'organization' && r.scope_id).map(r => r.scope_id as string)
      ));

      const [cohortsRes, orgsRes] = await Promise.all([
        cohortIds.length > 0
          ? supabase.from('cohorts').select('id, name, status').in('id', cohortIds)
          : Promise.resolve({ data: [] as any[], error: null }),
        orgIds.length > 0
          ? supabase.from('organizations').select('id, name').in('id', orgIds)
          : Promise.resolve({ data: [] as any[], error: null }),
      ]);

      const cohortMap = new Map<string, { name: string; status: string }>(
        (cohortsRes.data ?? []).map((c: any) => [c.id, { name: c.name, status: c.status }])
      );
      const orgMap = new Map<string, { name: string }>(
        (orgsRes.data ?? []).map((o: any) => [o.id, { name: o.name }])
      );

      // Filtrar por jerarquía cohorte > empresa > personal:
      // - Cohortes: solo si la cohorte sigue 'active' (frozen histórico se oculta).
      // - Empresa/Personal: solo si el usuario NO tiene ninguna cohorte activa.
      const hasActiveCohort = rows.some(r =>
        r.scope_type === 'cohort' && r.scope_id && cohortMap.get(r.scope_id)?.status === 'active'
      );

      const active: ActiveStreak[] = rows
        .filter(r => {
          if (r.scope_type === 'cohort') {
            return !!r.scope_id && cohortMap.get(r.scope_id)?.status === 'active';
          }
          if (r.scope_type === 'organization') {
            return !hasActiveCohort;
          }
          if (r.scope_type === 'personal') {
            return !hasActiveCohort;
          }
          return false;
        })
        .map(r => ({
          scope_type: r.scope_type as ActiveStreak['scope_type'],
          scope_id: r.scope_id,
          current_streak: r.current_streak ?? 0,
          max_streak: r.max_streak ?? 0,
          last_activity_date: r.last_activity_date,
          scope_label:
            r.scope_type === 'cohort'
              ? cohortMap.get(r.scope_id as string)?.name ?? 'Cohorte'
              : r.scope_type === 'organization'
                ? orgMap.get(r.scope_id as string)?.name ?? 'Empresa'
                : 'Personal',
        }))
        .sort((a, b) => b.current_streak - a.current_streak);

      return active;
    },
    enabled: !!user?.id,
  });
}

// Current cohort enrollment + schedule
export function useCohortEnrollment() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['cohort-enrollment', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohort_participants')
        .select(`
          cohort_id,
          enrolled_at,
          cohorts (
            id, name, start_date, end_date, demo_day_date, demo_day_link,
            status,
            programs ( id, name )
          )
        `)
        .eq('user_id', user!.id)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export function useUpcomingEvents(cohortId: string | undefined) {
  return useQuery({
    queryKey: ['upcoming-events', cohortId],
    queryFn: async () => {
      // Fecha LOCAL (toISOString es UTC y en Colombia después de las 7pm ya
      // devolvía mañana).
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const { data, error } = await supabase
        .from('cohort_schedule_items')
        .select('*')
        .eq('cohort_id', cohortId!)
        .gte('scheduled_date', today)
        .order('scheduled_date', { ascending: true })
        .order('start_hour', { ascending: true, nullsFirst: false })
        .order('start_minute', { ascending: true, nullsFirst: false })
        .limit(20);
      if (error) throw error;
      // Una sesión de hoy deja de ser "próxima" cuando termina (hora de fin, o
      // inicio + 2h si no tiene), no a medianoche.
      const nowMin = now.getHours() * 60 + now.getMinutes();
      return (data ?? []).filter((e: any) => {
        if (e.scheduled_date !== today || e.start_hour == null) return true;
        const startMin = e.start_hour * 60 + (e.start_minute ?? 0);
        let endMin = e.end_hour != null ? e.end_hour * 60 + (e.end_minute ?? 0) : startMin + 120;
        if (endMin <= startMin) endMin = startMin + 120;
        return endMin > nowMin;
      });
    },
    enabled: !!cohortId,
    // Re-evaluar periódicamente para que la tarjeta pase a la siguiente sesión
    // sin recargar la página.
    refetchInterval: 5 * 60 * 1000,
  });
}

// Content progress stats
export function useContentProgress() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['content-progress', user?.id],
    queryFn: async () => {
      // Avance contra el currículo obligatorio de la cohorte (no contra las filas
      // que el usuario abrió). Sin cohorte, cae al conteo de contenidos abiertos.
      const { data: curr } = await (supabase as any).rpc('get_users_curriculum_progress', {
        _user_ids: [user!.id],
      });
      const row = Array.isArray(curr) ? curr[0] : null;
      if (row && row.total_required > 0) {
        const total = row.total_required as number;
        const completed = row.completed_required as number;
        return { total, completed, percent: Math.round((completed / total) * 100) };
      }
      const { data, error } = await supabase
        .from('content_progress')
        .select('content_id, completed, progress_percent')
        .eq('user_id', user!.id);
      if (error) throw error;
      const total = data?.length ?? 0;
      const completed = data?.filter(c => c.completed).length ?? 0;
      return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
    },
    enabled: !!user?.id,
  });
}
